import React from 'react';

// Client-facing read-only view of a construction proposal: itemized scope +
// project total, with the 25% deposit / balance split spelled out.

const money = (v) =>
  `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const lineTotal = (r) => (Number(r.quantity) || 0) * (Number(r.unit_price) || 0);
const round2 = (n) => Math.round(n * 100) / 100;

const cell = { padding: '8px 10px', borderBottom: '1px solid #eef0f3', fontSize: 14 };

export default function ConstructionProposalSummary({ data, variant = 'construction' }) {
  const rows = Array.isArray(data?.line_items) ? data.line_items : [];
  const total = rows.reduce((s, r) => s + lineTotal(r), 0);
  const isHardware = variant === 'hardware';   // hardware = full invoice, no deposit/balance split
  const deposit = round2(total * 0.25);
  const balance = round2(total - deposit);

  if (!rows.length) {
    return <p style={{ color: '#64748b' }}>This proposal has no line items yet.</p>;
  }

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={cell}>Category</th>
            <th style={cell}>Description</th>
            <th style={{ ...cell, textAlign: 'right' }}>Qty</th>
            <th style={{ ...cell, textAlign: 'right' }}>Unit</th>
            <th style={{ ...cell, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={cell}>{r.category}</td>
              <td style={cell}>{r.description}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{r.quantity}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{money(r.unit_price)}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{money(lineTotal(r))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ marginTop: 16, maxWidth: 360, marginLeft: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 15 }}>
          <span>{isHardware ? 'Total' : 'Project total'}</span><strong>{money(total)}</strong>
        </div>
        {isHardware ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#065f46', fontSize: 15 }}>
            <span>Due on acceptance</span><strong>{money(total)}</strong>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#065f46', fontSize: 15 }}>
              <span>Deposit due now (25%)</span><strong>{money(deposit)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#475569' }}>
              <span>Balance (billed on completion)</span><strong>{money(balance)}</strong>
            </div>
          </>
        )}
      </div>

      {data?.notes && (
        <p style={{ marginTop: 14, color: '#475569', fontSize: 14 }}><strong>Notes:</strong> {data.notes}</p>
      )}

      <p style={{ marginTop: 14, fontSize: 13, color: '#64748b' }}>
        {isHardware ? (
          <>Accepting this proposal generates an <strong>invoice for the full amount ({money(total)})</strong>.</>
        ) : (
          <>Accepting this proposal generates a <strong>25% deposit invoice ({money(deposit)})</strong> to
          start the project. The remaining balance is billed when the work is complete.</>
        )}
      </p>
    </div>
  );
}
